// What the brand panel previews, derived from the wizard's answers — nothing
// here is invented: every line is an answer the person gave or a figure the
// API returned (rule 7). The hours chip summarises the open days exactly.

import { formatIraqiPhone, toArabicDigits } from '@/lib/digits';
import { iqd } from '@/lib/format';
import { countLabel, placeTerms, staffNoun, term, termProfile, type TermProfile } from '@/lib/terminology';
import type { PreviewData } from './components/LivePreview';
import { DONE, HANDOVER, PERIODS, PLACES, PROGRAMS, SHAPE1, SHAPE_COPY, TABLES } from './copy';
import type { BookingShape, TeamInvite } from './data/api';
import { WEEK, type HandoverDraft, type HoursDraft } from './state';

/** The terminology profile of the chosen category (clinic / default / gulf). */
export function profileOf(categoryId: string | undefined | null): TermProfile {
  return termProfile(categoryId ?? null, 'IQ');
}

/** «٤:٠٠ م» from 'HH:MM'. */
export function arTime(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const h = Number(m[1]);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${toArabicDigits(h12)}:${toArabicDigits(m[2])} ${h < 12 ? 'ص' : 'م'}`;
}

/**
 * The hours chip: «السبت – الخميس · ٤:٠٠ م – ٩:٠٠ م» when the open days are one
 * run sharing one range, «يومياً · …» when all seven do, the day names joined
 * otherwise (several ranges → the days only). Empty when no day is open.
 */
export function hoursSummary(h: HoursDraft, daily = 'يومياً', withTimes = true): string {
  const open = h.days.map((d, i) => ({ ...d, i })).filter((d) => d.open);
  if (open.length === 0) return '';
  const ranges = new Set(open.map((d) => `${d.from}-${d.to}`));
  const range = withTimes && ranges.size === 1 ? `${arTime(open[0].from)} – ${arTime(open[0].to)}` : null;
  const contiguous = open.every((d, k) => k === 0 || d.i === open[k - 1].i + 1);
  let days: string;
  if (open.length === 7) days = daily;
  else if (contiguous && open.length > 2) days = `${WEEK[open[0].i].name} – ${WEEK[open[open.length - 1].i].name}`;
  else days = open.map((d) => WEEK[d.i].name).join('، ');
  return range ? `${days} · ${range}` : days;
}

export interface HoursChipState {
  hours: HoursDraft;
  handover: HandoverDraft;
  /** Shape 3: the active periods the server holds («· فترتان», «· ٣ فترات»). */
  periodsCount?: number;
}

/**
 * The hours chip per shape: the weekly hours (staff, place, table), the work
 * days + the period count (business: «{days} · {n} فترات», dual «فترتان»),
 * the handover times (unit), none (seat: no hours, board 25 v2 shape--5).
 */
export function hoursChip(shape: BookingShape | null | undefined, s: HoursChipState): string | undefined {
  switch (shape) {
    case 'seat':
      return undefined;
    case 'unit':
      return HANDOVER.chip(arTime(s.handover.checkIn), arTime(s.handover.checkOut));
    case 'business': {
      const days = hoursSummary(s.hours, undefined, false);
      if (!days) return undefined;
      return s.periodsCount ? `${days} · ${countLabel(s.periodsCount, PERIODS.periodsWords)}` : days;
    }
    default:
      return hoursSummary(s.hours) || undefined;
  }
}

/**
 * The preview's «احجز …» per shape (board 25 v2 panel): staff «احجز» (clinic
 * «احجز زيارة»), place «احجز ملعباً» (the category's place noun), «احجز فترة»,
 * «احجز ليلة» (W9: «احجز يوماً» when every unit type is priced by the day),
 * «احجز مقعداً», «احجز طاولة».
 */
export function shapeCta(shape: BookingShape | null | undefined, terms: { categoryId?: string | null; unitsAllDay?: boolean }): string {
  switch (shape) {
    case 'place':
      return `احجز ${placeTerms(terms.categoryId).placeAcc}`;
    case 'business':
      return 'احجز فترة';
    case 'unit':
      return terms.unitsAllDay ? 'احجز يوماً' : 'احجز ليلة';
    case 'seat':
      return 'احجز مقعداً';
    case 'table':
      return 'احجز طاولة';
    default:
      return profileOf(terms.categoryId) === 'clinic' ? DONE.ctaClinic : DONE.cta;
  }
}

/** The server lists the shape steps hold (and the done screen reads back). */
export interface ShapeLists {
  categoryId?: string | null;
  places?: Array<{ active: boolean }>;
  bands?: unknown[];
  periods?: Array<{ name: string; active?: boolean }>;
  unitTypes?: Array<{ name: string; price_iqd: number; price_unit: string; active?: boolean }>;
  programs?: Array<{ weekly: boolean }>;
  tables?: Array<{ zone: string; active: boolean }>;
  sittingMin?: number;
  /** Shape 1: the team line (owner + invitees). */
  teamLine?: string;
}

/** The preview's shape line, per shape, from the server lists (rule 7: nothing drawn without them). */
export function shapeLine(shape: BookingShape | null | undefined, l: ShapeLists): string | undefined {
  switch (shape) {
    case 'place':
      return placesLine(l.categoryId, l.places?.length ?? 0, (l.bands?.length ?? 0) > 0);
    case 'business':
      return periodsLine((l.periods ?? []).filter((p) => p.active !== false));
    case 'unit':
      return unitLine(l.unitTypes ?? []);
    case 'seat':
      return programsLine(l.programs ?? []);
    case 'table':
      return tablesLine(l.tables ?? [], l.sittingMin ?? 90);
    default:
      return l.teamLine;
  }
}

/** 'HH:MM' from minutes of the day (1440 → 00:00). */
export function hhmm(min: number): string {
  const m = ((min % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** «٨:٠٠ ص – ٤:٠٠ م · ٢٥٬٠٠٠ د.ع» — one saved price band. */
export function bandSummary(b: { from_min: number; to_min: number; price_iqd: number }): string {
  return `${arTime(hhmm(b.from_min))} – ${arTime(hhmm(b.to_min))} · ${iqd(b.price_iqd)}`;
}

/** «ملعب واحد» / «ملعبان» / «٣ ملاعب» / «١١ ملعباً» — Arabic count agreement on the place noun. */
export function placeCount(n: number, categoryId: string | null | undefined): string {
  const t = placeTerms(categoryId);
  if (n === 1) return `${t.placeSing} واحد`;
  if (n === 2) return t.placeDual;
  const mod = n % 100;
  if (n >= 3 && mod >= 3 && mod <= 10) return `${toArabicDigits(n)} ${t.placePlural}`;
  return `${toArabicDigits(n)} ${t.placeAcc}`;
}

/** Shape 2: «٣ ملاعب · السعر بالساعة» when bands exist, else «٣ ملاعب»; nothing with no place. */
export function placesLine(categoryId: string | null | undefined, places: number, hasBands: boolean): string | undefined {
  if (places <= 0) return undefined;
  return hasBands ? `${placeCount(places, categoryId)} · ${PLACES.perHour}` : placeCount(places, categoryId);
}

/** Shape 4: «{first type name} · {price} د.ع / ليلة|يوم» (the first active type). */
export function unitLine(types: Array<{ name: string; price_iqd: number; price_unit: string; active?: boolean }>): string | undefined {
  const t = types.find((x) => x.active !== false);
  return t ? `${t.name} · ${iqd(t.price_iqd)} / ${t.price_unit === 'day' ? 'يوم' : 'ليلة'}` : undefined;
}

/** Shape 5: «٣ برامج بجلسات أسبوعية» when every program has a weekly series, else «٣ برامج». */
export function programsLine(programs: Array<{ weekly: boolean }>): string | undefined {
  if (programs.length === 0) return undefined;
  const n = countLabel(programs.length, PROGRAMS.programsWords);
  return programs.every((p) => p.weekly) ? `${n} ${PROGRAMS.weeklySeries}` : n;
}

/** Shape 6: «٤ طاولات في ٣ مناطق · الجلسة ٩٠ دقيقة» (active tables). */
export function tablesLine(tables: Array<{ zone: string; active: boolean }>, sittingMin: number): string | undefined {
  const active = tables.filter((t) => t.active);
  if (active.length === 0) return undefined;
  const zones = new Set(active.map((t) => t.zone)).size;
  return `${countLabel(active.length, TABLES.tablesWords)} في ${countLabel(zones, TABLES.zonesWords)} · ${TABLES.sitting(toArabicDigits(sittingMin))}`;
}

/** Shape 3: the period names joined with « · ». */
export function periodsLine(periods: Array<{ name: string }>): string | undefined {
  const names = periods.map((p) => p.name.trim()).filter(Boolean);
  return names.length ? names.join(' · ') : undefined;
}

/** The team line of the preview («الفريق: د. سارة · زينب · نور»): the owner's name then the invitees. */
export function teamLine(ownerName: string | null, invites: TeamInvite[]): string | undefined {
  const names = [ownerName, ...invites.map((i) => (i.name && i.name.trim()) || formatIraqiPhone(i.phone))].filter((x): x is string => !!x && !!x.trim());
  if (names.length <= 1 && invites.length === 0) return undefined;
  return SHAPE1.teamLine(names.join(' · '));
}

export interface PreviewInput {
  name: string;
  categoryId: string;
  categoryLabel: string;
  city: string;
  /** The first branch's name (the branches question on); '' when one place. */
  branch?: string;
  area: string;
  phone: string;
  hours: HoursDraft;
  /** The category's shape (null = not chosen: the weekly hours). */
  shape?: BookingShape | null;
  handover?: HandoverDraft;
  slug: string | null;
  ownerName: string | null;
  invites: TeamInvite[];
  /** The server lists of the shape step (shapes 2–6) — the line, the CTA, the periods count. */
  lists?: ShapeLists;
  /** How far the wizard is: which answers to show (everything up to and including this one). */
  upTo: 'name' | 'type' | 'city' | 'branches' | 'address' | 'bphone' | 'hours' | 'shape' | 'done';
  still?: boolean;
}

const ORDER: PreviewInput['upTo'][] = ['name', 'type', 'city', 'branches', 'address', 'bphone', 'hours', 'shape', 'done'];

/** W9: every unit type is priced by the day (car rental) → «احجز يوماً». */
function unitsAllDay(l: ShapeLists | undefined): boolean {
  const types = (l?.unitTypes ?? []).filter((t) => t.active !== false);
  return types.length > 0 && types.every((t) => t.price_unit === 'day');
}

export function buildPreview(p: PreviewInput): PreviewData {
  const at = ORDER.indexOf(p.upTo);
  const has = (k: PreviewInput['upTo']) => ORDER.indexOf(k) <= at;
  const staffish = !p.shape || p.shape === 'staff';
  const out: PreviewData = {
    name: p.name.trim() || undefined,
    cat: has('type') && p.categoryLabel ? p.categoryLabel : undefined,
    city: has('city') && p.city.trim() ? p.city.trim() : undefined,
    branch: has('branches') && p.branch?.trim() ? p.branch.trim() : undefined,
    addr: has('address') && p.area.trim() ? p.area.trim() : undefined,
    phone: has('bphone') && p.phone.trim() ? formatIraqiPhone(p.phone) : undefined,
    hours: has('hours')
      ? hoursChip(p.shape, {
          hours: p.hours,
          handover: p.handover ?? { checkIn: '15:00', checkOut: '12:00' },
          periodsCount: has('shape') ? (p.lists?.periods ?? []).filter((x) => x.active !== false).length : undefined,
        })
      : undefined,
    line: has('shape') ? shapeLine(p.shape, { ...p.lists, categoryId: p.categoryId, teamLine: teamLine(p.ownerName, p.invites) }) : undefined,
    lineIcon: staffish ? 'users' : 'calendar',
    cta: shapeCta(p.shape, { categoryId: p.categoryId, unitsAllDay: unitsAllDay(p.lists) }),
    slug: p.slug,
    still: p.still,
  };
  return out;
}

/** The shape-1 sentence of §14.3, worded from the terminology (the board draws the clinic form). */
export function staffShapeSentence(categoryId: string | undefined | null): string {
  const profile = profileOf(categoryId);
  const staff = staffNoun(profile, categoryId ?? null);
  const indef = staff.label.replace(/^ال/, '');
  const dual = staff.feminine ? `${indef.replace(/ة$/, '')}تان` : `${indef}ان`;
  const appt = profile === 'clinic' ? 'زيارة' : 'موعداً';
  return `${term('customer', profile)} يحجز ${appt} مع ${indef}، ويختار ${staff.label} إن سمحت وكان عندك ${dual} فأكثر.`;
}

/**
 * The type-step sentence after «الحجز في هذه الفئة يكون على {word}:» — the
 * drawn staff sentence for shape 1; for shapes 2–6 the shape step's drawn sub
 * (ruling R7), with the place noun from the terminology for shape 2.
 */
export function typeShapeSentence(shape: BookingShape, categoryId: string | undefined | null): string {
  if (shape === 'staff') return staffShapeSentence(categoryId);
  if (shape === 'place') return SHAPE_COPY.place.sub(categoryId ?? '', profileOf(categoryId));
  return SHAPE_COPY[shape].sub();
}

/** The team-step note (board shape--1), worded from the terminology. */
export function teamNote(categoryId: string | undefined | null, customersAccusative: string): string {
  const profile = profileOf(categoryId);
  const staff = staffNoun(profile, categoryId ?? null);
  const indef = staff.label.replace(/^ال/, '');
  const dual = staff.feminine ? `${indef.replace(/ة$/, '')}تين` : `${indef}ين`;
  return SHAPE1.note(customersAccusative, staff.label, dual);
}
